import { useMemo, useState } from "react";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { api } from "../lib/api";
import { useAsync } from "../lib/hooks";
import { useI18n, type MessageKey } from "../lib/i18n";
import { COUNTRY_META } from "../lib/country";
import type { Brand } from "../lib/types";
import { Button, Card, ErrorBox, Field, Loading, Modal } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { newestFirst } from "../lib/format";

export function Brands() {
  const { t, fmtDate } = useI18n();
  const { data, loading, error, reload } = useAsync(() => api.brands.list(), []);
  const [editing, setEditing] = useState<Brand | "new" | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const brands = data ?? [];

  async function remove(b: Brand) {
    if (!confirm(t("brands.confirmDelete", { name: b.name }))) return;
    setBusy(`del-${b.id}`);
    setActionError(null);
    try {
      await api.brands.remove(b.id);
      await reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const columns = useMemo<GridColDef<Brand>[]>(
    () => [
      {
        field: "name",
        headerName: t("brands.name"),
        flex: 1,
        minWidth: 140,
        renderCell: ({ row }) => (
          <span className="mono" style={{ fontWeight: 600 }}>
            {row.name}
          </span>
        ),
      },
      {
        field: "country",
        headerName: t("table.country"),
        flex: 0.7,
        minWidth: 120,
        renderCell: ({ row }) => (
          <span>
            {COUNTRY_META[row.country]?.flag ?? ""} {t(`country.${row.country}` as MessageKey)}
          </span>
        ),
      },
      {
        field: "note",
        headerName: t("brands.note"),
        flex: 1.4,
        minWidth: 160,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.note || "—"}
          </Typography>
        ),
      },
      {
        field: "linkCount",
        headerName: t("brands.links"),
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" label={row.linkCount} />
        ),
      },
      {
        field: "userCount",
        headerName: t("brands.users"),
        flex: 0.5,
        minWidth: 90,
        renderCell: ({ row }) => (
          <Chip size="small" variant="outlined" label={row.userCount} />
        ),
      },
      {
        field: "createdAt",
        headerName: t("brands.created"),
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Typography variant="body2" color="text.secondary" noWrap>
            {fmtDate(row.createdAt)}
          </Typography>
        ),
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 56,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<EditOutlinedIcon fontSize="small" />}
            label={t("common.edit")}
            onClick={() => setEditing(row)}
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon fontSize="small" />}
            label={t("common.delete")}
            disabled={busy === `del-${row.id}`}
            onClick={() => remove(row)}
            showInMenu
          />,
        ],
      },
    ],
    [t, fmtDate, busy],
  );

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <h1>{t("brands.title")}</h1>
          <p className="muted">{t("brands.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={() => setEditing("new")}>
          {t("brands.add")}
        </Button>
      </header>

      {error && <ErrorBox message={error} />}
      {actionError && <ErrorBox message={actionError} />}

      <Card>
        {loading ? (
          <Loading />
        ) : (
          <DataTable
            rows={newestFirst(brands, "createdAt")}
            columns={columns}
            showToolbar
            loading={busy !== null}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          />
        )}
      </Card>

      {editing && (
        <BrandForm
          brand={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function BrandForm({
  brand,
  onClose,
  onSaved,
}: {
  brand: Brand | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useI18n();
  const [name, setName] = useState(brand?.name ?? "");
  const [note, setNote] = useState(brand?.note ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (brand) {
        await api.brands.update(brand.id, { name: name.trim(), note: note.trim() });
      } else {
        await api.brands.create({ name: name.trim(), note: note.trim() || undefined });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={brand ? t("brands.formEdit", { name: brand.name }) : t("brands.formNew")}
      onClose={onClose}
    >
      <form onSubmit={submit} className="form">
        {error && <ErrorBox message={error} />}
        <Field label={t("brands.name")} hint={t("brands.nameHint")}>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus />
        </Field>
        <Field label={t("brands.note")} hint={t("brands.noteHint")}>
          <input value={note} onChange={e => setNote(e.target.value)} />
        </Field>
        <div className="form-actions">
          <Button type="button" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={busy}>
            {brand ? t("common.save") : t("brands.create")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
